/**
 * The standing-orders block on the objective panel: in full while he is finding his feet, then folded to the
 * one rule that can lose this mission (missionOrders.ts). Seen on screen, mission 36: six lines of prose
 * filling a quarter of the display for three minutes of holding a ford.
 *
 * In full for the first `FULL_SECONDS` on the ground, and again whenever the map key is held (`setHeld`);
 * folded the rest of the time, with a one-word reminder of the key that brings them back. A lost objective
 * takes the block over entirely and stays red: that path (OBJECTIVE FAILED) must never be folded away.
 */

import { TICK_RATE } from '../../sim/types';
import type { MissionDef, World } from '../../sim/types';
import { el, show, Shown } from './dom';
import { keyOrdersLine, ordersLine } from './missionOrders';
import { type HudTextSize, ordersWidth } from './textSize';

/** How long the whole block stands after touchdown. Long enough to read six lines, short enough to forgive. */
export const FULL_SECONDS = 12;

export class OrdersView {
  readonly el: HTMLElement;
  private readonly text: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly shown = new Shown();
  private readonly full: string | null;
  private readonly key: string | null;
  private held = false;
  private size: HudTextSize = 1;
  private failed = false;
  /** The tick he first stood on the ground; the block's twelve seconds are counted from there. */
  private groundAt = -1;

  constructor(def: MissionDef, private hintKey = 'TAB') {
    this.full = ordersLine(def);
    this.key = keyOrdersLine(def);
    this.el = el('div', 'hud-orders');
    this.text = el('div', 'hud-orders-text');
    this.hint = el('div', 'hud-orders-hint hud-hidden');
    this.el.append(this.text, this.hint);
    show(this.el, this.full !== null);
  }

  /** The map key is down: the whole block, for as long as he holds it. */
  setHeld(held: boolean): void {
    this.held = held;
  }

  /** The label of whatever re-shows the orders, as the bindings call it. */
  setKey(key: string): void {
    this.hintKey = key === '' ? '-' : key;
    this.shown.changed('hint', '');
  }

  setTextSize(size: HudTextSize): void {
    this.size = size;
  }

  /** The objective is lost: the reason replaces the orders and nothing folds it away again. */
  fail(reason: string): void {
    this.failed = true;
    this.el.classList.add('hud-orders-failed');
    this.el.classList.remove('hud-orders-open');
    show(this.el, true);
    this.hint.textContent = '';
    show(this.hint, false);
    this.text.textContent = reason;
    this.text.style.maxWidth = `${ordersWidth(this.size, true)}px`;
    this.shown.changed('body', reason);
  }

  update(world: World): void {
    if (this.failed || this.full === null) return;
    const open = this.held || this.groundSeconds(world) < FULL_SECONDS;
    const body = open ? this.full : this.key ?? '';
    if (this.shown.changed('body', body)) {
      this.text.textContent = body;
      show(this.el, body !== '' || open);
    }
    if (this.shown.changed('open', open)) {
      this.el.classList.toggle('hud-orders-open', open);
      this.text.style.maxWidth = `${ordersWidth(this.size, open)}px`;
    }
    // Once they have folded away, the panel says how to get them back - and then stops saying it.
    const hint = open ? '' : `[${this.hintKey}] ORDERS`;
    if (this.shown.changed('hint', hint)) {
      this.hint.textContent = hint;
      show(this.hint, hint !== '');
    }
  }

  /** Seconds since he reached the ground; negative while he is still in the air, so the block waits for him. */
  private groundSeconds(world: World): number {
    if (world.insertion.phase !== 'ground') {
      this.groundAt = -1;
      return -1;
    }
    // A restart winds the clock back: take the new world's tick as the moment he landed.
    if (this.groundAt < 0 || world.tick < this.groundAt) this.groundAt = world.tick;
    return (world.tick - this.groundAt) / TICK_RATE;
  }
}
